"use client";

import { useEffect, useRef, useState } from "react";
import TiltCard from "./TiltCard";

export interface StepCaption {
  /** Second of the clip at which this line comes up. */
  at: number;
  text: string;
}

export interface StepperStep {
  title: string;
  description: string;
  videoSrc: string;
  /** A still of the clip, shown until it plays (or instead, if it can't). */
  posterSrc: string;
  /** What the clip shows, line by line, timed to its playback. */
  captions: StepCaption[];
}

interface HowItWorksStepperProps {
  steps: StepperStep[];
}

/** Strips leading numbering ("1. ", "2) ") since the stepper renders its own. */
export function stripStepNumber(title: string): string {
  return title.replace(/^\s*\d+\s*[.)\-:]\s*/, "");
}

/** How long a step stays up when its clip can't play (autoplay refused, load error). */
export const FALLBACK_STEP_MS = 8000;

const NEXT_KEYS = ["ArrowDown", "ArrowRight"];
const PREV_KEYS = ["ArrowUp", "ArrowLeft"];
const START = { t: 0, d: 0 };

/**
 * Three-step walkthrough: a vertical step list next to the clip for the
 * active step. The clip is the clock: the bar and the captions follow its
 * playback and the next step starts when it ends, so a slow network or a
 * stall can never cut a clip short. Where it can't play at all, a fixed-length
 * timer cycles the steps over their posters instead. Playback runs only
 * while the section is on screen and holds while a keyboard user is in it.
 */
export default function HowItWorksStepper({ steps }: HowItWorksStepperProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  // Bumped on every step change, so even a lone step restarts its clip.
  const [run, setRun] = useState(0);
  const [inView, setInView] = useState(false);
  // Set once the section has been on screen; the clip keeps its source from
  // then on, so scrolling away pauses it instead of rewinding it.
  const [armed, setArmed] = useState(false);
  const [paused, setPaused] = useState(false);
  const [fallback, setFallback] = useState(false);
  const [clock, setClock] = useState(START);

  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      entries => {
        const visible = entries.some(entry => entry.isIntersecting);
        setInView(visible);
        if (visible) setArmed(true);
      },
      { threshold: 0.35 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const running = inView && !paused;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !armed) return;
    if (!running) {
      video.pause();
      return;
    }
    video.play().catch((error: unknown) => {
      // A pause or the next step interrupted it: playback itself works.
      if (error instanceof DOMException && error.name === "AbortError") return;
      setFallback(true);
    });
  }, [armed, running, activeIndex, run]);

  const active = steps[activeIndex];
  if (!active) return null;

  const goTo = (index: number) => {
    setActiveIndex(index);
    setRun(value => value + 1);
    setClock(START);
  };
  // Picking the step that is already showing leaves its clip alone.
  const select = (index: number) => {
    if (index !== activeIndex) goTo(index);
  };
  const advance = () => goTo((activeIndex + 1) % steps.length);

  const progress = clock.d > 0 ? Math.min(clock.t / clock.d, 1) : 0;
  const captionIndex = active.captions.reduce(
    (current, caption, index) => (clock.t >= caption.at ? index : current),
    0
  );
  const hasCaptions = steps.some(step => step.captions.length > 0);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const offset = NEXT_KEYS.includes(event.key)
      ? 1
      : PREV_KEYS.includes(event.key)
        ? -1
        : 0;
    if (!offset) return;
    event.preventDefault();
    const next = (activeIndex + offset + steps.length) % steps.length;
    select(next);
    document.getElementById(`step-tab-${next}`)?.focus();
  };

  return (
    <div
      ref={rootRef}
      className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-10"
      // Keyboard focus holds the walkthrough so the step can be read; the
      // browser's :focus-visible heuristic tells it apart from the focus a
      // tap or click leaves behind, so every focus re-decides, and any press
      // hands control back to the clip. No hover pause: a resting mouse made
      // the stepper look stuck.
      onFocusCapture={event =>
        setPaused((event.target as HTMLElement).matches(":focus-visible"))
      }
      onPointerDownCapture={() => setPaused(false)}
      onBlurCapture={event => {
        if (!rootRef.current?.contains(event.relatedTarget as Node | null)) {
          setPaused(false);
        }
      }}
    >
      <div
        role="tablist"
        aria-orientation="vertical"
        aria-label={stripStepNumber(active.title)}
        className="flex flex-col gap-3 lg:col-span-5"
      >
        {steps.map((step, index) => {
          const isActive = index === activeIndex;
          const title = stripStepNumber(step.title);
          return (
            <button
              key={step.videoSrc}
              id={`step-tab-${index}`}
              type="button"
              role="tab"
              // The description is repeated in the panel, so the tab's name
              // stays short.
              aria-label={title}
              aria-selected={isActive}
              aria-controls="step-panel"
              tabIndex={isActive ? 0 : -1}
              onClick={() => select(index)}
              onKeyDown={handleKeyDown}
              // Every step is a clear glass card; the active one is lit with
              // the blue tint rather than washed white.
              className={`glass group rounded-glassInner p-3 pr-6 text-left transition-[background-color] duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] ${
                isActive ? "tint-blue pb-5" : "hover:bg-white/30"
              }`}
            >
              <span className="flex items-center gap-4">
                <span
                  aria-hidden="true"
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-signal-mono text-[13px] font-medium transition-colors duration-500 ${
                    isActive
                      ? "btn-glass-primary"
                      : "glass-inset text-signal-muted group-hover:text-signal-ink"
                  }`}
                >
                  {index + 1}
                </span>
                <span
                  className={`text-[17px] font-semibold tracking-[-0.01em] transition-colors duration-500 ${
                    isActive
                      ? "text-signal-ink"
                      : "text-signal-muted group-hover:text-signal-ink"
                  }`}
                >
                  {title}
                </span>
              </span>

              {isActive && (
                <span aria-hidden="true" className="block pl-14">
                  {/* Every description shares one cell, so the open card is
                      as tall as the longest and the list keeps its height
                      from step to step. */}
                  <span className="mt-2 grid text-pretty text-[15px] leading-[1.65] text-signal-muted">
                    {steps.map((other, otherIndex) => (
                      <span
                        key={other.videoSrc}
                        className={`[grid-area:1/1] ${
                          otherIndex === index
                            ? "animate-signal-rise motion-reduce:animate-none"
                            : "invisible"
                        }`}
                      >
                        {other.description}
                      </span>
                    ))}
                  </span>
                  <span className="mt-4 block h-1 overflow-hidden rounded-full bg-signal-line">
                    {fallback ? (
                      <span
                        // Restarts the timer for a lone step too.
                        key={run}
                        data-testid="step-progress"
                        onAnimationEnd={advance}
                        // No motion-reduce:animate-none: without a playable
                        // clip this animation is the stepper's clock.
                        className="block h-full origin-left animate-signal-grow rounded-full bg-signal-accent"
                        style={{
                          animationDuration: `${FALLBACK_STEP_MS}ms`,
                          animationTimingFunction: "linear",
                          animationPlayState: running ? "running" : "paused",
                        }}
                      />
                    ) : (
                      <span
                        data-testid="step-progress"
                        // timeupdate fires about four times a second; the
                        // transition fills the gaps.
                        className="block h-full origin-left rounded-full bg-signal-accent transition-transform duration-300 ease-linear"
                        style={{ transform: `scaleX(${progress})` }}
                      />
                    )}
                  </span>
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div
        id="step-panel"
        role="tabpanel"
        aria-labelledby={`step-tab-${activeIndex}`}
        className="relative lg:col-span-7"
      >
        <div className="sr-only">
          <p>{active.description}</p>
          {active.captions.length > 0 && (
            <ol>
              {active.captions.map(caption => (
                <li key={caption.at}>{caption.text}</li>
              ))}
            </ol>
          )}
        </div>
        {/* Same device as the hero's video (HeroSignal): a tilted leaf card
            with a header row, hovering over a soft floor shadow. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-[10%] -bottom-10 h-16 rounded-[50%] bg-signal-ink/25 blur-2xl"
        />
        <TiltCard
          restX={3}
          restY={-9}
          strength={5}
          lift={18}
          className="glass-flat relative rounded-glassPanel p-3 sm:p-4"
        >
          <div
            aria-hidden="true"
            className="flex items-center justify-between gap-3 pb-3 pl-3 pr-1 pt-1.5"
          >
            <span className="glass-inset rounded-full px-4 py-1.5 font-signal-mono text-[11px] text-signal-muted">
              heyfinn.ai
            </span>
            <span className="pr-2 font-signal-mono text-[11px] text-signal-muted">
              {String(activeIndex + 1).padStart(2, "0")} /{" "}
              {String(steps.length).padStart(2, "0")}
            </span>
          </div>

          {/* The screen: small corners under the header, and the card's own
              curves (less the padding) where it meets the card edge. */}
          <div className="relative aspect-video overflow-hidden rounded-[28px] bg-signal-paper">
            {/* No loop: the clip plays once and its end starts the next step */}
            <video
              key={`${active.videoSrc}#${run}`}
              ref={videoRef}
              src={armed ? active.videoSrc : undefined}
              poster={active.posterSrc}
              onTimeUpdate={event =>
                setClock({
                  t: event.currentTarget.currentTime,
                  d: event.currentTarget.duration,
                })
              }
              onEnded={advance}
              // Playback works after all (e.g. after a tap): the clip takes
              // the clock back from the fallback timer.
              onPlaying={() => setFallback(false)}
              onError={() => setFallback(true)}
              className="h-full w-full animate-signal-rise object-cover motion-reduce:animate-none"
              muted
              playsInline
              preload="none"
            />
            {/* The clip's stage is pale, so without a hairline the screen
                melts into the white card around it. */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 rounded-[inherit] ring-1 ring-inset ring-signal-ink/10"
            />
          </div>

          {hasCaptions && (
            <div
              aria-hidden="true"
              className="flex items-start gap-3 px-3 pb-1.5 pt-3.5"
            >
              <span className="mt-[7px] flex shrink-0 gap-1.5">
                {active.captions.map((caption, index) => (
                  <span
                    key={caption.at}
                    className={`h-1.5 w-1.5 rounded-full transition-colors duration-500 ${
                      index === captionIndex
                        ? "bg-signal-accent"
                        : index < captionIndex
                          ? "bg-signal-accent/40"
                          : "bg-signal-line"
                    }`}
                  />
                ))}
              </span>
              {/* All captions share one cell, so the card keeps its height
                  as they change. */}
              <span className="grid min-w-0 flex-1 text-[14px] font-semibold leading-snug text-signal-ink">
                {steps.flatMap((step, stepIndex) =>
                  step.captions.map((caption, index) => (
                    <span
                      key={`${step.videoSrc}#${caption.at}`}
                      data-testid={
                        stepIndex === activeIndex && index === captionIndex
                          ? "step-caption"
                          : undefined
                      }
                      className={`[grid-area:1/1] ${
                        stepIndex === activeIndex && index === captionIndex
                          ? "animate-signal-rise motion-reduce:animate-none"
                          : "invisible"
                      }`}
                    >
                      {caption.text}
                    </span>
                  ))
                )}
              </span>
            </div>
          )}
        </TiltCard>
      </div>
    </div>
  );
}
